import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe, faGavel } from '@fortawesome/free-solid-svg-icons';
import { faLinkedin, faYoutube } from '@fortawesome/free-brands-svg-icons';

const FooterHome = ({ language, setLanguage }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const footerTranslations = {
    es: {
      termsPrivacy: 'Términos & Privacidad',
      toggleLanguageBtn: 'Cambiar idioma',
      english: 'English',
      spanish: 'Español',
    },
    en: {
      termsPrivacy: 'Terms & Privacy',
      toggleLanguageBtn: 'Switch language',
      english: 'English',
      spanish: 'Spanish',
    },
  };

  const t = footerTranslations[language];

  const handleToggleLanguage = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleLanguageSelect = (lang) => {
    setLanguage(lang);
    setIsDropdownOpen(false);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 600);
    };

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const styles = {
    footer: {
      backgroundColor: '#ff2300',
      padding: isMobile ? '10px 20px' : '20px 40px',
    },
    footerContainer: {
      display: 'flex',
      flexDirection: 'row', // Siempre en fila
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'nowrap', // No envolver
      gap: isMobile ? '10px' : '20px',
    },
    footerLogo: {
      height: isMobile ? '20px' : '24px',
      flexShrink: 0,
    },
    logoLink: {
      textDecoration: 'none',
      flexShrink: 0,
    },
    footerLinks: {
      display: 'flex',
      flexDirection: 'row',
      gap: isMobile ? '10px' : '20px',
      alignItems: 'center',
      color: '#ffffff',
      fontSize: isMobile ? '14px' : '16px',
      position: 'relative', // Para posicionar el dropdown
      flexShrink: 0,
    },
    footerLink: {
      textDecoration: 'none',
      color: '#ffffff',
      flexShrink: 0,
    },
    footerIcon: {
      fontSize: isMobile ? '18px' : '20px',
    },
    footerSocial: {
      display: 'flex',
      flexDirection: 'row',
      gap: isMobile ? '10px' : '15px',
      marginTop: '0',
      flexShrink: 0,
    },
    socialIcon: {
      fontSize: isMobile ? '20px' : '24px',
      color: '#ffffff',
      cursor: 'pointer',
    },
    languageButton: {
      backgroundColor: 'transparent',
      border: 'none',
      color: '#ffffff',
      fontSize: isMobile ? '14px' : '16px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      position: 'relative',
      padding: '5px',
      flexShrink: 0,
    },
    dropdown: {
      position: 'absolute',
      bottom: '100%', // Abrir hacia arriba
      right: '0',
      backgroundColor: '#ffffff',
      color: '#000000',
      border: '1px solid #ddd',
      borderRadius: '4px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
      zIndex: 1000,
      minWidth: '100px',
      opacity: isDropdownOpen ? 1 : 0,
      transform: isDropdownOpen ? 'translateY(0)' : 'translateY(-10px)',
      transition: 'opacity 0.3s ease, transform 0.3s ease',
      pointerEvents: isDropdownOpen ? 'auto' : 'none',
    },
    dropdownItem: {
      padding: '8px 10px',
      cursor: 'pointer',
      borderBottom: '1px solid #eee',
      whiteSpace: 'nowrap',
    },
    dropdownItemLast: {
      padding: '8px 10px',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    },
  };

  return (
    <footer style={styles.footer}>
      <div style={styles.footerContainer}>
        {/* Logo en blanco como enlace a la página de inicio */}
        <Link to="/" style={styles.logoLink}>
          <img
            src="/BetherLogowhite.png"
            alt="Bether Logo"
            style={styles.footerLogo}
          />
        </Link>

        {/* Sección de links */}
        <div style={styles.footerLinks} ref={dropdownRef}>
          {setLanguage && (
            <div style={{ position: 'relative' }}>
              <button
                onClick={handleToggleLanguage}
                style={styles.languageButton}
                aria-haspopup="true"
                aria-expanded={isDropdownOpen}
                aria-label={t.toggleLanguageBtn}
              >
                <FontAwesomeIcon icon={faGlobe} style={styles.footerIcon} />
              </button>
              <div style={styles.dropdown}>
                <div
                  style={styles.dropdownItem}
                  onClick={() => handleLanguageSelect('es')}
                >
                  {language === 'es' ? '✓ ' : ''}
                  {t.spanish}
                </div>
                <div
                  style={styles.dropdownItemLast}
                  onClick={() => handleLanguageSelect('en')}
                >
                  {language === 'en' ? '✓ ' : ''}
                  {t.english}
                </div>
              </div>
            </div>
          )}
          <Link
            to="/legal"
            style={styles.footerLink}
            aria-label={t.termsPrivacy}
          >
            <FontAwesomeIcon icon={faGavel} style={styles.footerIcon} />
          </Link>
        </div>

        {/* Redes sociales */}
        <div style={styles.footerSocial}>
          <a
            href="https://www.linkedin.com/company/bether.ai"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="LinkedIn"
          >
            <FontAwesomeIcon icon={faLinkedin} style={styles.socialIcon} />
          </a>
          <a
            href="https://www.youtube.com/@BetherFramework"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="YouTube"
          >
            <FontAwesomeIcon icon={faYoutube} style={styles.socialIcon} />
          </a>
        </div>
      </div>
    </footer>
  );
};

export default FooterHome;
